import React from 'react';

import classnames from 'classnames';

import HorizontalScroller from 'components/uiLibrary/HorizontalScroller';
import Image from 'components/uiLibrary/Image';
import LinkButton, { TextButton } from 'components/uiLibrary/LinkButton';
import Typography from 'components/uiLibrary/Typography';

import { SECTIONS, COMPONENTS } from 'components/Globals/Analytics/constants';
import GlobalSearch from 'components/Globals/Layout/Header/GlobalSearch';
import HeaderSearch from 'components/Globals/Layout/Header/HeaderSearch';

import { TP } from 'constants/index';

import useDeviceTypeLayouts from 'utils/hooks/useDeviceTypeLayouts';
import { useDialogs } from 'utils/hooks/useDialogs';
import useNavigate from 'utils/hooks/useNavigate';
import { useQuery } from 'utils/react-query';

import queries from 'containers/Globals/queries';

import { useTranslation } from 'src/i18n';

import { ENTITY_TYPES } from 'operabase-router/constants';

import LogoImage from 'public/images/logo_white.png';

import Banner from '../Common/Banner';
import TrustedBy from '../TrustedBy';

import classes from './HeroSection.module.scss';

const trackingData = {
  section: SECTIONS.HOME_PAGE_BANNER,
};

const oldSearchTrackingData = {
  section: SECTIONS.OLD_GLOBAL_SEARCH,
};

const BACKGROUND_IMAGE =
  'https://images.operabase.com/image/fetch/w_3840/https://public.operabase.com/images/banners/operabase_bg_20241022T113632524Z.webp';

const BG_IMAGE_SRC_SETS = [
  {
    path:
      'https://images.operabase.com/image/fetch/f_avif,w_640/https://public.operabase.com/images/banners/operabase_bg_20241022T113632524Z.webp',
    type: 'image/avif',
    width: 640,
  },
  {
    path:
      'https://images.operabase.com/image/fetch/f_avif,w_750/https://public.operabase.com/images/banners/operabase_bg_20241022T113632524Z.webp',
    type: 'image/avif',
    width: 750,
  },
  {
    path:
      'https://images.operabase.com/image/fetch/f_avif,w_828/https://public.operabase.com/images/banners/operabase_bg_20241022T113632524Z.webp',
    type: 'image/avif',
    width: 828,
  },
  {
    path:
      'https://images.operabase.com/image/fetch/f_avif,w_1080/https://public.operabase.com/images/banners/operabase_bg_20241022T113632524Z.webp',
    type: 'image/avif',
    width: 1080,
  },
  {
    path:
      'https://images.operabase.com/image/fetch/f_avif,w_1200/https://public.operabase.com/images/banners/operabase_bg_20241022T113632524Z.webp',
    type: 'image/avif',
    width: 1200,
  },
  {
    path:
      'https://images.operabase.com/image/fetch/f_avif,w_1280/https://public.operabase.com/images/banners/operabase_bg_20241022T113632524Z.webp',
    type: 'image/avif',
  },
  {
    path:
      'https://images.operabase.com/image/fetch/f_webp,w_640/https://public.operabase.com/images/banners/operabase_bg_20241022T113632524Z.webp',
    type: 'image/webp',
    width: 640,
  },
  {
    path:
      'https://images.operabase.com/image/fetch/f_webp,w_750/https://public.operabase.com/images/banners/operabase_bg_20241022T113632524Z.webp',
    type: 'image/webp',
    width: 750,
  },
  {
    path:
      'https://images.operabase.com/image/fetch/f_webp,w_828/https://public.operabase.com/images/banners/operabase_bg_20241022T113632524Z.webp',
    type: 'image/webp',
    width: 828,
  },
  {
    path:
      'https://images.operabase.com/image/fetch/f_webp,w_1080/https://public.operabase.com/images/banners/operabase_bg_20241022T113632524Z.webp',
    type: 'image/webp',
    width: 1080,
  },
  {
    path:
      'https://images.operabase.com/image/fetch/f_webp,w_1200/https://public.operabase.com/images/banners/operabase_bg_20241022T113632524Z.webp',
    type: 'image/webp',
    width: 1200,
  },
  {
    path:
      'https://images.operabase.com/image/fetch/f_webp,w_1280/https://public.operabase.com/images/banners/operabase_bg_20241022T113632524Z.webp',
    type: 'image/webp',
  },
];

const BANNERS = [
  {
    title: `${TP}.PROAUDIENCE`,
    isProTools: false,
    items: [
      `${TP}.FN_TICKETS`,
      `${TP}.FN_WATCH_ONLINE_VIDEOS`,
      `${TP}.ARCHIVES`,
      `${TP}.m_REVIEWS`,
      `${TP}.m_ARTISTS`,
      `${TP}.TRAILERS`,
      `${TP}.m_HIGHLIGHTS`,
      `${TP}.NEW_AND_RARE`,
    ],
  },
  {
    title: `${TP}.FOR_PROFESSIONALS`,
    isProTools: true,
    items: [
      `${TP}.CASTING_TALENT`,
      `${TP}.AUDIENCE_GROWTH`,
      `${TP}.PRODUCTION_PLANNING_RENTALS_CO_PRODUCTIONS`,
      `${TP}.CAREER_MANAGEMENT`,
      `${TP}.AGENCY_ROSTERS`,
    ],
  },
];

const Genres = () => {
  const navigate = useNavigate();
  const { data: menuItems } = useQuery(queries.getMenuItems({ type: 'ALL' }));

  return (
    <HorizontalScroller hideArrow>
      <ul className={classes.genres}>
        {(menuItems?.data?.genre || []).slice(0, 14).map(genre => (
          <li key={genre?.id}>
            <LinkButton
              isLink
              variant="text"
              styles={{ root: classes.genre }}
              {...navigate.getLinkProps({ entity: genre, entityType: ENTITY_TYPES.WORK_TYPE })}
              trackingData={{
                ...trackingData,
                component: COMPONENTS.GENRE,
                subComponent: COMPONENTS.NAVIGATE_CTA,
              }}
            >
              <Typography size={12} color="white" className={classes.genre} textTransform="uppercase">
                {genre?.name}
              </Typography>
            </LinkButton>
          </li>
        ))}
      </ul>
    </HorizontalScroller>
  );
};

const Search = () => {
  const { isDesktop } = useDeviceTypeLayouts();
  const { t } = useTranslation('NS_APP_GLOBALS');

  if (!isDesktop) {
    return (
      <div className={classes.searchSection}>
        <GlobalSearch />
      </div>
    );
  }

  return (
    <div className={classes.searchSection}>
      <div className={classnames(classes.search, classes.oldSearch)}>
        <Typography color="quaternary" size={12} weight="bold">
          {t(`${TP}.SEARCH_EVERYTHING`)}
        </Typography>
        <HeaderSearch trackingData={oldSearchTrackingData} />
      </div>
      <div className={classes.search}>
        <Typography color="quaternary" size={12} weight="bold">
          {t(`${TP}.ADVANCED_PERFORMANCE_SEARCH`)}
        </Typography>
        <GlobalSearch />
      </div>
    </div>
  );
};

const HeroSection = () => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const navigate = useNavigate();
  const { setIsMenuOpen } = useDialogs();
  const { isDesktop } = useDeviceTypeLayouts();
  const homePageLinkProps = navigate.getLinkProps({ onlyLinkProps: true });

  const handleClickBanner = isProTools => {
    setIsMenuOpen({ isOpen: true, isProTools });
  };

  return (
    <div className={classes.heroSection}>
      <Image
        src={BACKGROUND_IMAGE}
        srcSet={BG_IMAGE_SRC_SETS}
        alt="Operabase Banner"
        lazy={false}
        disableCloudinary
        disableNextImage
        preload
        className={classes.bg}
      />
      <div className={classes.overlay} />
      <div className={classes.mainContent}>
        <div className={classes.topSection}>
          <div>
            <TextButton
              {...homePageLinkProps}
              isLink
              styles={{ root: classes.logoLink }}
              trackingData={{
                ...trackingData,
                component: COMPONENTS.OPERABASE_BUTTON,
              }}
              disableUnderline
            >
              <span className={classes.logoImg}>
                <Image
                  src={LogoImage}
                  alt="Operabase Home"
                  lazy={false}
                  height={17}
                  disableCloudinary
                  disableNextImage
                />
                <Typography size={isDesktop ? 12 : 10} color="quaternary">
                  {t(`${TP}.LP_SINCE`)}
                </Typography>
              </span>
              <Typography variant="h1" color="quaternary" size={isDesktop ? 14 : 12} secondaryFont>
                <span className={classes.visuallyHidden}>{t(`${TP}.m_OPERABASE`)}: </span>
                {t(`${TP}.FN_GLOBAL_REFERENCE_PERFORMING_ARTS`)}
              </Typography>
            </TextButton>
          </div>
          <div className={classes.banners}>
            {BANNERS.map((item, index) => (
              <Banner
                key={index}
                {...item}
                onClick={() => handleClickBanner(item.isProTools)}
                trackingData={trackingData}
              />
            ))}
          </div>
        </div>
        <Genres />
        <Search />
        <div className={classes.trustedBy}>
          <TrustedBy trackingData={trackingData} />
        </div>
      </div>
    </div>
  );
};

export default HeroSection;
